// The server's notion of whitespace, for mirroring the keys it builds from text.
//
// Rust's `str::split_whitespace`, `str::trim` and `char::is_whitespace` all use the
// Unicode White_Space property. JavaScript's `\s` and `String.prototype.trim` use
// ECMAScript's WhiteSpace + LineTerminator instead, and the two sets differ:
//
// - U+FEFF (ZERO WIDTH NO-BREAK SPACE) is in `\s` but is not White_Space, so the server
//   keeps it as an ordinary character inside a word.
// - U+0085 (NEXT LINE) is White_Space but not in `\s`, so the server splits on it.
//
// Anything that has to agree with the server on where words break uses this class
// rather than `\s`.

/**
 * Every code point with the Unicode White_Space property (PropList.txt, unchanged
 * since Unicode 6.3 dropped U+180E), which is exactly what Rust's
 * `char::is_whitespace` accepts.
 */
export const WHITE_SPACE_CLASS =
  "\\u0009-\\u000D\\u0020\\u0085\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000"

const WHITE_SPACE_RUN = new RegExp(`[${WHITE_SPACE_CLASS}]+`, "gu")
const LEADING_OR_TRAILING = new RegExp(
  `^[${WHITE_SPACE_CLASS}]+|[${WHITE_SPACE_CLASS}]+$`,
  "gu"
)

/** Rust `str::trim`: `text` without leading and trailing White_Space. */
export const trimWhiteSpace = (text: string) =>
  text.replace(LEADING_OR_TRAILING, "")

/** Rust `str::split_whitespace`: the non-empty runs between White_Space. */
export const splitWhiteSpace = (text: string): string[] => {
  const trimmed = trimWhiteSpace(text)
  return trimmed ? trimmed.split(WHITE_SPACE_RUN) : []
}

/**
 * The server's `normalize_translation_text`, the form translation-memory text is keyed
 * and compared under:
 * `text.split_whitespace().collect::<Vec<_>>().join(" ").to_lowercase()`.
 *
 * `toLowerCase` and Rust's `to_lowercase` both apply Unicode's full, locale-independent
 * lowercase mapping, final sigma included, so they agree up to the Unicode version each
 * runtime ships.
 */
export const normalizeTranslationText = (text: string) =>
  splitWhiteSpace(text).join(" ").toLowerCase()
